import { useState } from 'react'
import { Alert, Box, Button, Heading, Text, VStack } from '@chakra-ui/react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { FiArrowLeft } from 'react-icons/fi'
import { useGrantSession } from '../../hooks/useGrantSession'
import { useGrantCampaign } from '../../hooks/useGrantCampaign'
import { useGrantProfile } from '../../hooks/useGrantProfile'
import { useGrantImpact } from '../../hooks/useGrantImpact'
import { useMyTurfs } from '../../hooks/useCanvasser'
import { useSync } from '../../providers/SyncProvider'
import { readChosenAssignment } from '../../lib/walkPrefs'
import { turfIdOfKey } from '../../lib/todayAssignments'
import { PointOfContactCard } from '../../components/canvasser/PointOfContactCard'
import { PortraitEditor } from '../../components/canvasser/profile/PortraitEditor'
import { ProfileDetailsForm } from '../../components/canvasser/profile/ProfileDetailsForm'
import { TodaySoFar } from '../../components/canvasser/profile/TodaySoFar'
import { ReturnLinkCard } from '../../components/canvasser/profile/ReturnLinkCard'
import { ImpactCard } from '../../components/canvasser/profile/ImpactCard'

/**
 * Canvasser profile: who's signed in on this device, their photo + details (Phase 7, online-only edits),
 * their campaign and today's turf, today's numbers, their impact (Phase 8, online read), the way-back link, and log out. The name comes from
 * GET me/profile, falling back to the /me identity offline.
 * Logout best-effort-flushes the outbound queue, revokes the server session, and
 * always attempts to blunt-wipe offline data + clear the mirror (see useGrantSession).
 * The soft note below is keyed deterministically off the `{ revokeFailed, wipeFailed }`
 * values `logout()` returns — not a post-navigation refetch or a separate async path,
 * which would race the navigate() call below (§E).
 */
export function CanvasserProfilePage() {
  const { identity, logout } = useGrantSession()
  const campaign = useGrantCampaign()
  const { data: profile } = useGrantProfile()
  const impact = useGrantImpact()
  const { isOnline } = useSync()
  const { data: turfs } = useMyTurfs(campaign?.id)
  const navigate = useNavigate()
  const [revokeFailed, setRevokeFailed] = useState(false)
  const [wipeFailed, setWipeFailed] = useState(false)
  const firstName = profile?.firstName ?? identity?.subjectFirstName
  const lastName = profile?.lastName ?? identity?.subjectLastName
  const name = [firstName, lastName].filter(Boolean).join(' ')
  const chosen = campaign ? turfIdOfKey(readChosenAssignment(campaign.id)) : null
  const todaysTurf = turfs?.find(t => t.turfId === chosen) ?? turfs?.[0]
  const isOffline = !isOnline

  const onLogout = async () => {
    const { revokeFailed, wipeFailed } = await logout()
    if (revokeFailed || wipeFailed) {
      setRevokeFailed(revokeFailed)
      setWipeFailed(wipeFailed)
      return
    }
    navigate('/canvasser/redeem-needed', { replace: true })
  }

  return (
    <VStack align="stretch" gap={6} p={6} maxW="30rem" mx="auto">
      {/* [Spec D4] There is no bottom nav — this is the way back. */}
      <Button asChild variant="ghost" alignSelf="start" minH="48px" px={0}>
        <RouterLink to="/canvasser"><FiArrowLeft /> Back to my walk</RouterLink>
      </Button>
      <PortraitEditor
        name={name || 'Canvasser'}
        src={profile ? profile.portraitUrl : (identity?.portraitUrl ?? null)}
        isOffline={isOffline}
      />
      <Box>
        <Text color="fg.muted">Signed in as</Text>
        <Text fontSize="lg" fontWeight="semibold">{name || 'Canvasser'}</Text>
      </Box>
      <ProfileDetailsForm profile={profile} isOffline={isOffline} />
      {campaign && (
        <Box borderWidth="1px" borderColor="border" borderRadius="lg" bg="surface" p={4}>
          <Heading size="sm" mb={1}>Your campaign</Heading>
          <Text>{campaign.label}</Text>
          {todaysTurf && <Text color="fg.muted">Today's turf: {todaysTurf.turfLabel}</Text>}
        </Box>
      )}
      {campaign && <PointOfContactCard poc={campaign.poc} />}
      <TodaySoFar today={profile?.today} />
      <ImpactCard impact={impact.data} isLoading={impact.isLoading} isError={impact.isError} isOffline={isOffline} />
      <ReturnLinkCard />
      <Button colorPalette="brand" variant="outline" minH="48px" onClick={onLogout}>
        Log out
      </Button>
      {wipeFailed && (
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          <Alert.Content>
            We couldn't fully clear this device's data. Please try logging out again, or contact your
            organizer if this keeps happening.
          </Alert.Content>
        </Alert.Root>
      )}
      {revokeFailed && !wipeFailed && (
        <Alert.Root status="warning" borderRadius="md">
          <Alert.Indicator />
          <Alert.Content>
            You're signed out on this device and your list has been wiped. The campaign may still show
            this session as active until it expires.
          </Alert.Content>
        </Alert.Root>
      )}
      <Text color="fg.muted" fontSize="sm" borderTopWidth="1px" borderColor="border.muted" pt={4}>
        This walk list is provided for this campaign's canvassing only. Don't copy, share, or keep it
        after your work is done — it stays the property of the campaign and the state.
      </Text>
    </VStack>
  )
}
